import { HTTPError } from "ky";

import type { ApiError } from "~/shared/types/api-types.ts";
import {
	extractApiError,
	extractFromTopLevelArray,
	isJsonContentType,
} from "~/shared/utils/extract-api-error.ts";

/**
 * Generic portal fallback message shown for unexpected (5xx) failures.
 * Mirrors the legacy LTL `MessageModalComponent.defaultErrorText` for the
 * internal employee experience.
 */
export const LTL_PORTAL_ERROR_MESSAGE =
	"LTL Portal encountered a problem. We apologize for the inconvenience. Please try your request again or, if the problem persists, contact tsupport@tql.com or (513) 831-2600 ext. 52198 for assistance.";

/**
 * External-facing fallback message used by Tender flows / external customers.
 * Mirrors the legacy `MessageModalComponent.defaultErrorText` for external users.
 */
export const TENDER_ERROR_MESSAGE =
	"LTL within TQL TRAX encountered a problem. We apologize for the inconvenience. Please try your request again or, if the problem persists, contact a TQL representative at (800) 580-3101 ext. 56400.";

/**
 * Returns the correct portal fallback message for the current user type,
 * mirroring the legacy `MessageModalComponent.defaultErrorText` behavior
 * which showed different contact details for internal employees vs. external
 * customers.
 */
export const getLtlFallbackMessage = (isInternal: boolean): string =>
	isInternal ? LTL_PORTAL_ERROR_MESSAGE : TENDER_ERROR_MESSAGE;

/** Status returned to the UI when the backend reports an eTag conflict (HTTP 412). */
export const QUOTE_REFRESH_REQUIRED_STATUS = 412;

const QUOTE_ALREADY_TENDERED_PREFIX = "Quote already tendered";

/**
 * Mirrors the legacy LTL behavior: when any error message in a 400 response
 * starts with "Quote already tendered (PONumber=12345)", surface a friendlier
 * message that calls out the existing PO number.
 */
export const rewriteTenderedQuoteMessage = (message: string): string => {
	const lines = message
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
	const tenderedLine = lines.find((line) =>
		line.startsWith(QUOTE_ALREADY_TENDERED_PREFIX),
	);
	if (!tenderedLine) {
		return message;
	}
	const equalsIndex = tenderedLine.indexOf("=");
	if (equalsIndex === -1) {
		return message;
	}
	const poNumber = tenderedLine
		.slice(equalsIndex + 1)
		.replace(/\)\s*$/, "")
		.trim();
	if (poNumber.length === 0) {
		return message;
	}
	return `This quote has already been tendered as PO# ${poNumber} and cannot be changed. Please check the load board for further details.`;
};

/**
 * LTL-specific error extractor. The legacy LTL backend returns several
 * 400 response shapes that the default JSON `{ message, errors }` extractor
 * doesn't handle. Tries those shapes first, then falls back to the shared
 * extractor:
 *   - Plain-text body (`Content-Type: text/plain`)
 *   - Top-level JSON array of strings: `["err1", "err2"]`
 *   - Top-level JSON array of objects: `[{ message: "err1" }, ...]`
 */
const extractLegacyShape = async (
	httpError: HTTPError,
): Promise<ApiError | null> => {
	const status = httpError.response.status;
	const response = httpError.response.clone();
	const contentType = response.headers.get("content-type") ?? "";

	if (contentType.includes("text/plain")) {
		const rawText = await response.text();
		const text = rawText.trim();
		if (text.length > 0) {
			return { success: false, error: text, details: text, status };
		}
		return null;
	}

	if (isJsonContentType(contentType)) {
		const body: unknown = await response.json();
		if (Array.isArray(body)) {
			const messages = extractFromTopLevelArray(body);
			if (messages.length > 0) {
				return {
					success: false,
					error: messages.join("\n"),
					details: body,
					status,
				};
			}
		}
	}

	return null;
};

export const extractLtlApiError = async (
	error: unknown,
): Promise<ApiError | null> => {
	if (error instanceof HTTPError) {
		try {
			const legacy = await extractLegacyShape(error);
			if (legacy) {
				return legacy;
			}
		} catch {
			// Fall through to the shared extractor on any parse failure.
		}
	}

	return extractApiError(error);
};

const getHttpStatus = (error: unknown): number | undefined => {
	if (error instanceof HTTPError) {
		return error.response.status;
	}
	return undefined;
};

/**
 * Creates a standardized error handler for TanStack Query mutations against
 * LTL endpoints.
 *
 * Behavior matches the legacy `BaseActionComponent.handleErrorModals`:
 *   - Extracts errors from JSON object bodies, plain-text bodies, and
 *     top-level array bodies (strings or `{ message }` objects).
 *   - Rewrites "Quote already tendered (PONumber=…)" into a user-friendly
 *     message that calls out the existing PO number.
 *   - Surfaces the HTTP status on the returned `ApiError` so callers can
 *     react to 412 Precondition Failed (eTag conflict) by prompting the
 *     user to refresh.
 *   - Substitutes the generic portal fallback message for any 5xx response,
 *     so users do not see raw "Internal server error" / stack-trace text.
 *
 * @param fallbackMessage - The default error message to use when API error
 *                          extraction fails or when the response is a 5xx.
 * @returns An error handler suitable for the `onError` option of
 *          `getAndValidate` / `putAndValidate` / `postAndValidate`.
 */
export const createLtlMutationErrorHandler = (fallbackMessage: string) => {
	return async (error: unknown): Promise<ApiError> => {
		const apiError = await extractLtlApiError(error);

		if (apiError === null) {
			const status = getHttpStatus(error);
			const result: ApiError = {
				success: false,
				error: fallbackMessage,
				details: error,
			};
			if (status !== undefined) {
				result.status = status;
			}
			return result;
		}

		// For server errors (5xx), show the generic portal fallback message
		// instead of raw API messages, matching legacy LTL behavior.
		const isServerError =
			apiError.status !== undefined && apiError.status >= 500;

		return {
			...apiError,
			error: isServerError
				? fallbackMessage
				: rewriteTenderedQuoteMessage(apiError.error),
		};
	};
};
